import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";

const Field = ({ label, children, hint }) => (
  <div>
    <label className="label block mb-2">{label}</label>
    {children}
    {hint && <div className="text-xs text-[var(--muted)] mt-1">{hint}</div>}
  </div>
);
const inputCls = "w-full bg-white border hairline px-3 py-2.5 outline-none focus:border-[var(--ink)] transition-colors text-sm";

function Toggle({ label, value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)} data-testid={testid}
      className="flex items-center justify-between w-full border hairline px-4 py-3 bg-white hover:border-[var(--ink)] transition-colors">
      <span className="font-display uppercase tracking-[0.06em] text-sm">{label}</span>
      <span className="w-11 h-6 rounded-full relative transition-colors" style={{ background: value ? "var(--ink)" : "var(--line)" }}>
        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: value ? "22px" : "2px" }} />
      </span>
    </button>
  );
}

const emptyCity = () => ({ name: "", country: "", lat: "", lng: "", bbox: [], zoom: 12, published: false });
const emptyRecord = () => ({
  sticker_id: "", city_id: "", lat: "", lng: "", status: "placed",
  artwork_slug: "", preview_image: "", practice_url: "", date_placed: "", date_found: "",
});

export default function AllCityAdmin({ works, flash }) {
  const [cities, setCities] = useState([]);
  const [records, setRecords] = useState([]);
  const [tab, setTab] = useState("records");
  const [cityFilter, setCityFilter] = useState("all");
  const [editingCity, setEditingCity] = useState(null);    // { form, isNew }
  const [editingRecord, setEditingRecord] = useState(null);
  const [lookupQ, setLookupQ] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/admin/field-records").then((r) => setRecords(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const cityName = (id) => cities.find((c) => c.id === id)?.name || "—";
  const setCF = (k, v) => setEditingCity((ec) => ({ ...ec, form: { ...ec.form, [k]: v } }));
  const setRF = (k, v) => setEditingRecord((er) => ({ ...er, form: { ...er.form, [k]: v } }));

  // ---- cities ----
  const saveCity = async (e) => {
    e.preventDefault();
    const f = editingCity.form;
    const body = { name: f.name, country: f.country, lat: Number(f.lat), lng: Number(f.lng), bbox: f.bbox || [], zoom: Number(f.zoom) || 12, published: !!f.published };
    try {
      if (editingCity.isNew) await api.post("/admin/cities", body);
      else await api.put(`/admin/cities/${f.id}`, body);
      flash("City saved ✓"); load(); setEditingCity(null);
    } catch (e2) { flash(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const deleteCity = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Its field records must be removed first.`)) return;
    try { await api.delete(`/admin/cities/${c.id}`); flash("City deleted"); load(); }
    catch (e2) { flash(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const setDefault = async (c) => {
    try {
      await api.put("/settings/allcity", { data: { default_city_id: c.id } });
      flash(`${c.name} is now the default city ✓`); load();
    } catch { flash("Could not set default city"); }
  };
  const lookup = async () => {
    if (!lookupQ.trim()) return;
    setLookupBusy(true);
    try {
      const { data } = await api.get("/admin/geocode", { params: { city: lookupQ.trim() } });
      setEditingCity((ec) => ({
        ...ec,
        form: { ...ec.form, name: ec.form.name || data.city, country: data.country || ec.form.country, lat: data.lat, lng: data.lng, bbox: data.bbox },
      }));
      flash(`Located: ${data.display_name}`);
    } catch (e2) { flash(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setLookupBusy(false); }
  };

  // ---- field records ----
  const saveRecord = async (e) => {
    e.preventDefault();
    const f = editingRecord.form;
    const body = { ...f, lat: Number(f.lat), lng: Number(f.lng) };
    try {
      if (editingRecord.isNew) await api.post("/admin/field-records", body);
      else await api.put(`/admin/field-records/${f.id}`, body);
      flash("Field record saved ✓"); load(); setEditingRecord(null);
    } catch (e2) { flash(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const deleteRecord = async (r) => {
    if (!window.confirm(`Delete ${r.sticker_id}?`)) return;
    await api.delete(`/admin/field-records/${r.id}`); flash("Record deleted"); load();
  };
  const uploadPreview = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setRF("preview_image", data.url);
    } catch { flash("Upload failed"); } finally { setUploading(false); }
  };
  const pickArchive = (w) => {
    setEditingRecord((er) => ({ ...er, form: { ...er.form, artwork_slug: w.slug, preview_image: "" } }));
    setPickerOpen(false);
  };

  const list = records.filter((r) => cityFilter === "all" || r.city_id === cityFilter);

  // ---------- record editor ----------
  if (editingRecord) {
    const f = editingRecord.form;
    const linkedArt = works.find((w) => w.slug === f.artwork_slug);
    const effImg = f.preview_image || linkedArt?.image || "";
    return (
      <form onSubmit={saveRecord} className="max-w-3xl" data-testid="record-editor">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display font-700 text-3xl uppercase">{editingRecord.isNew ? "Add field record" : `Edit ${f.sticker_id}`}</h1>
          <button type="button" onClick={() => setEditingRecord(null)} className="label hover:opacity-50">← Back</button>
        </div>

        <section className="mb-10">
          <div className="label mb-4 pb-2 border-b hairline">Identification</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Sticker ID" hint="e.g. BLITZ 057 — must be unique">
              <input value={f.sticker_id} onChange={(e) => setRF("sticker_id", e.target.value)} data-testid="record-sticker-id" className={inputCls} required />
            </Field>
            <Field label="City">
              <select value={f.city_id} onChange={(e) => setRF("city_id", e.target.value)} data-testid="record-city" className={inputCls} required>
                <option value="">Select a city…</option>
                {cities.map((c) => <option key={c.id} value={c.id}>{c.name}{c.country ? ` — ${c.country}` : ""}</option>)}
              </select>
            </Field>
          </div>
        </section>

        <section className="mb-10">
          <div className="label mb-4 pb-2 border-b hairline">Coordinates</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Latitude" hint="e.g. 28.63156 — must fall inside the city's boundaries">
              <input type="number" step="any" value={f.lat} onChange={(e) => setRF("lat", e.target.value)} data-testid="record-lat" className={inputCls} required />
            </Field>
            <Field label="Longitude" hint="e.g. 77.22043">
              <input type="number" step="any" value={f.lng} onChange={(e) => setRF("lng", e.target.value)} data-testid="record-lng" className={inputCls} required />
            </Field>
          </div>
        </section>

        <section className="mb-10">
          <div className="label mb-4 pb-2 border-b hairline">Discovery status</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Status">
              <select value={f.status} onChange={(e) => setRF("status", e.target.value)} data-testid="record-status" className={inputCls}>
                <option value="placed">PLACED / UNFOUND</option>
                <option value="found">FOUND / DOCUMENTED</option>
              </select>
            </Field>
            <Field label="PRACTICE documentation URL" hint={f.status === "found" ? "Required for FOUND / DOCUMENTED — opens in a new tab when the marker is clicked" : "Required once the sticker is documented as found"}>
              <input value={f.practice_url} onChange={(e) => setRF("practice_url", e.target.value)} data-testid="record-practice-url"
                placeholder="https://rcktarchives.com/practice/…" className={inputCls} />
            </Field>
            <Field label="Date placed"><input type="date" value={f.date_placed} onChange={(e) => setRF("date_placed", e.target.value)} data-testid="record-date-placed" className={inputCls} /></Field>
            <Field label="Date found" hint="May remain empty until discovered"><input type="date" value={f.date_found} onChange={(e) => setRF("date_found", e.target.value)} data-testid="record-date-found" className={inputCls} /></Field>
          </div>
        </section>

        <section className="mb-10">
          <div className="label mb-4 pb-2 border-b hairline">Artwork preview (shown beside the cursor on the map)</div>
          {effImg && (
            <div className="mb-4 flex items-start gap-4">
              <img src={effImg} alt="preview" className="w-32 border hairline" data-testid="record-preview-img" />
              <div className="text-xs text-[var(--muted)] mt-1">
                {f.preview_image ? "Custom photograph" : linkedArt ? `Archive: ${linkedArt.number_label} — ${linkedArt.title}` : ""}
                <button type="button" onClick={() => setEditingRecord((er) => ({ ...er, form: { ...er.form, artwork_slug: "", preview_image: "" } }))}
                  data-testid="record-clear-image" className="label block mt-3 hover:text-red-700">Clear image</button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-4">
            <button type="button" onClick={() => setPickerOpen(true)} data-testid="record-archive-picker"
              className="border hairline bg-white px-5 py-3 label hover:border-[var(--ink)] transition-colors">Select from official archive</button>
            <label className="border hairline bg-white px-5 py-3 label hover:border-[var(--ink)] transition-colors cursor-pointer">
              {uploading ? "Uploading…" : "Upload a photograph"}
              <input type="file" accept="image/*" onChange={uploadPreview} data-testid="record-upload" className="hidden" />
            </label>
          </div>
          <p className="text-xs text-[var(--muted)] mt-3">An uploaded photograph takes precedence on the map; the archive reference is retained.</p>
        </section>

        <div className="flex gap-4">
          <button type="submit" data-testid="record-save"
            className="bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:opacity-80 transition-opacity">
            {editingRecord.isNew ? "Create record" : "Save changes"}</button>
          <button type="button" onClick={() => setEditingRecord(null)} className="label">Cancel</button>
        </div>

        {pickerOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(26,26,26,0.55)" }} data-testid="archive-picker">
            <div className="bg-[var(--paper)] border hairline max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="label">Select from the official archive</div>
                <button type="button" onClick={() => setPickerOpen(false)} data-testid="archive-picker-close" className="label hover:opacity-50">Close ✕</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {works.filter((w) => w.image).map((w) => (
                  <button type="button" key={w.slug} onClick={() => pickArchive(w)} data-testid={`archive-pick-${w.slug}`}
                    className="text-left border hairline bg-white hover:border-[var(--ink)] transition-colors">
                    <img src={w.image} alt={w.number_label} className="w-full aspect-square object-cover" />
                    <div className="p-2 label">{w.number_label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </form>
    );
  }

  // ---------- city editor ----------
  if (editingCity) {
    const f = editingCity.form;
    return (
      <form onSubmit={saveCity} className="max-w-3xl" data-testid="city-editor">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display font-700 text-3xl uppercase">{editingCity.isNew ? "Add city" : `Edit ${f.name}`}</h1>
          <button type="button" onClick={() => setEditingCity(null)} className="label hover:opacity-50">← Back</button>
        </div>

        <section className="mb-10">
          <div className="label mb-4 pb-2 border-b hairline">Find the city</div>
          <Field label="City lookup" hint="Searches OpenStreetMap and fills centre, country and boundaries — you can adjust everything below before saving">
            <div className="flex gap-3">
              <input value={lookupQ} onChange={(e) => setLookupQ(e.target.value)} data-testid="city-lookup-input"
                placeholder="e.g. Delhi" className={inputCls} />
              <button type="button" onClick={lookup} disabled={lookupBusy} data-testid="city-lookup-btn"
                className="shrink-0 bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-xs px-6 py-3 hover:opacity-80 transition-opacity disabled:opacity-40">
                {lookupBusy ? "Looking up…" : "Look up"}</button>
            </div>
          </Field>
        </section>

        <section className="mb-10">
          <div className="label mb-4 pb-2 border-b hairline">City details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Name"><input value={f.name} onChange={(e) => setCF("name", e.target.value)} data-testid="city-name" className={inputCls} required /></Field>
            <Field label="Country"><input value={f.country} onChange={(e) => setCF("country", e.target.value)} data-testid="city-country" className={inputCls} /></Field>
            <Field label="Centre latitude"><input type="number" step="any" value={f.lat} onChange={(e) => setCF("lat", e.target.value)} data-testid="city-lat" className={inputCls} required /></Field>
            <Field label="Centre longitude"><input type="number" step="any" value={f.lng} onChange={(e) => setCF("lng", e.target.value)} data-testid="city-lng" className={inputCls} required /></Field>
            <Field label="Initial zoom" hint="11–13 works well for a city">
              <input type="number" step="0.5" min="2" max="18" value={f.zoom} onChange={(e) => setCF("zoom", e.target.value)} data-testid="city-zoom" className={inputCls} />
            </Field>
            <Field label="Boundaries" hint={f.bbox?.length === 4 ? "Set automatically by the lookup — field records outside this box are rejected" : "No boundaries set — run the lookup to enable boundary validation"}>
              <input value={f.bbox?.length === 4 ? `W ${f.bbox[0].toFixed(3)} · S ${f.bbox[1].toFixed(3)} · E ${f.bbox[2].toFixed(3)} · N ${f.bbox[3].toFixed(3)}` : "—"} readOnly className={inputCls + " opacity-60"} data-testid="city-bbox" />
            </Field>
          </div>
          <div className="mt-5 max-w-xs">
            <Toggle label="Published" value={!!f.published} onChange={(v) => setCF("published", v)} testid="city-published-toggle" />
          </div>
        </section>

        <div className="flex gap-4">
          <button type="submit" data-testid="city-save"
            className="bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:opacity-80 transition-opacity">
            {editingCity.isNew ? "Create city" : "Save changes"}</button>
          <button type="button" onClick={() => setEditingCity(null)} className="label">Cancel</button>
        </div>
      </form>
    );
  }

  // ---------- lists ----------
  return (
    <div data-testid="allcity-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-700 text-3xl uppercase">All City</h1>
        {tab === "records" ? (
          <button onClick={() => setEditingRecord({ form: { ...emptyRecord(), city_id: cityFilter !== "all" ? cityFilter : (cities[0]?.id || "") }, isNew: true })}
            data-testid="add-field-record"
            className="bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-xs px-5 py-3 hover:opacity-80 transition-opacity">+ Add field record</button>
        ) : (
          <button onClick={() => { setLookupQ(""); setEditingCity({ form: emptyCity(), isNew: true }); }}
            data-testid="add-city"
            className="bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-xs px-5 py-3 hover:opacity-80 transition-opacity">+ Add city</button>
        )}
      </div>

      <div className="flex gap-1 mb-8">
        {[["records", "Field records"], ["cities", "Cities"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`allcity-tab-${k}`}
            className="font-display uppercase tracking-[0.06em] text-sm px-4 py-2 transition-colors"
            style={{ opacity: tab === k ? 1 : 0.45, background: tab === k ? "var(--surface)" : "transparent" }}>{l}</button>
        ))}
      </div>

      {tab === "records" && (
        <div>
          <div className="mb-6">
            <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} data-testid="records-filter-city" className={inputCls + " w-auto"}>
              <option value="all">All cities</option>
              {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="border hairline bg-white">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 border-b hairline label">
              <div className="col-span-3">Sticker ID</div><div className="col-span-2">City</div><div className="col-span-2">Status</div>
              <div className="col-span-3">Coordinates</div><div className="col-span-2 text-right">Actions</div>
            </div>
            {list.map((r) => (
              <div key={r.id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b hairline items-center text-sm" data-testid={`record-row-${r.sticker_id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="md:col-span-3 font-display">{r.sticker_id}</div>
                <div className="md:col-span-2 text-[var(--muted)]">{cityName(r.city_id)}</div>
                <div className="md:col-span-2">
                  <span className="label" style={{ color: r.status === "found" ? "#B3001B" : "var(--muted)" }}>
                    {r.status === "found" ? "● Found" : "● Placed"}
                  </span>
                </div>
                <div className="md:col-span-3 text-[var(--muted)] text-xs">{Number(r.lat).toFixed(5)}, {Number(r.lng).toFixed(5)}</div>
                <div className="md:col-span-2 flex gap-3 md:justify-end">
                  <button onClick={() => setEditingRecord({ form: { ...emptyRecord(), ...r }, isNew: false })} data-testid={`record-edit-${r.id}`} className="label hover:opacity-50">Edit</button>
                  <button onClick={() => deleteRecord(r)} data-testid={`record-delete-${r.id}`} className="label hover:text-red-700">Delete</button>
                </div>
              </div>
            ))}
            {list.length === 0 && <div className="px-4 py-8 label">No field records yet.</div>}
          </div>
        </div>
      )}

      {tab === "cities" && (
        <div className="border hairline bg-white">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 border-b hairline label">
            <div className="col-span-3">City</div><div className="col-span-2">Country</div><div className="col-span-2">Records</div>
            <div className="col-span-2">Status</div><div className="col-span-3 text-right">Actions</div>
          </div>
          {cities.map((c) => (
            <div key={c.id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b hairline items-center text-sm" data-testid={`city-row-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="md:col-span-3 font-display">{c.name} {c.is_default && <span className="label ml-2" style={{ color: "#B3001B" }}>Default</span>}</div>
              <div className="md:col-span-2 text-[var(--muted)]">{c.country || "—"}</div>
              <div className="md:col-span-2 text-[var(--muted)]">{c.record_count}</div>
              <div className="md:col-span-2 label">{c.published ? "● Published" : "○ Unpublished"}</div>
              <div className="md:col-span-3 flex flex-wrap gap-3 md:justify-end">
                {!c.is_default && <button onClick={() => setDefault(c)} data-testid={`city-set-default-${c.id}`} className="label hover:opacity-50">Set default</button>}
                <button onClick={() => { setLookupQ(c.name); setEditingCity({ form: { ...emptyCity(), ...c }, isNew: false }); }} data-testid={`city-edit-${c.id}`} className="label hover:opacity-50">Edit</button>
                <button onClick={() => deleteCity(c)} data-testid={`city-delete-${c.id}`} className="label hover:text-red-700">Delete</button>
              </div>
            </div>
          ))}
          {cities.length === 0 && <div className="px-4 py-8 label">No cities yet.</div>}
        </div>
      )}
    </div>
  );
}
